import { Auth0Client } from "@auth0/auth0-spa-js";
import axios from "axios";
import { apiConfig, auth0Config } from "../config";
import { NotificationPreferences } from "../hooks/useNotificationPreferences";
import {
  NotificationCountsResponse,
  NotificationQuery,
  UserNotification,
} from "../types/notification";
import { createReference } from "../utils/DataTransformationUtils";
import { getBasicAuthConfig } from "./authUtils";

class NotificationApi {
  private auth0Client: Auth0Client | null = null;

  constructor() {
    this.auth0Client = new Auth0Client({
      redirect_uri: window.location.origin,
      ...auth0Config,
    });
  }

  async getNotifications(
    query: NotificationQuery = {},
    signal?: AbortSignal,
  ): Promise<UserNotification[]> {
    const config = await getBasicAuthConfig(this.auth0Client);
    const params = new URLSearchParams();

    if (query.userId) params.append("userId", query.userId);
    if (query.type) params.append("type", query.type);
    if (query.read !== undefined) params.append("read", String(query.read));
    if (query.limit) params.append("limit", String(query.limit));
    if (query.offset) params.append("offset", String(query.offset));

    return axios
      .get<
        UserNotification[]
      >(`${apiConfig.notificationServiceBase}/notifications?${params.toString()}`, { ...config, signal })
      .then((res) => res.data);
  }

  async getNotificationCounts(
    userId: string,
  ): Promise<NotificationCountsResponse> {
    const config = await getBasicAuthConfig(this.auth0Client);
    const params = new URLSearchParams();

    if (userId) params.append("userId", userId);

    return axios
      .get<NotificationCountsResponse>(
        `${apiConfig.notificationServiceBase}/notifications/notification-counts?${params.toString()}`,
        config,
      )
      .then((res) => res.data)
      .catch((error) => {
        console.error("Error getting notification counts", error);
        return { total: 0, read: 0, unread: 0 };
      });
  }

  async markAsRead(id: string, userId: string): Promise<UserNotification> {
    const config = await getBasicAuthConfig(this.auth0Client);
    const params = new URLSearchParams();

    if (userId) params.append("userId", userId);

    return axios
      .put<UserNotification>(
        `${apiConfig.notificationServiceBase}/notifications/${id}/read?${params.toString()}`,
        {},
        config,
      )
      .then((res) => res.data);
  }

  async markAllAsRead(userId?: string): Promise<number> {
    const config = await getBasicAuthConfig(this.auth0Client);
    const params = new URLSearchParams();

    if (userId) params.append("userId", userId);

    return axios
      .put<{
        count: number;
      }>(
        `${apiConfig.notificationServiceBase}/notifications/read-all?${params.toString()}`,
        {},
        config,
      )
      .then((res) => res.data.count);
  }

  async getNotificationPreferences(
    userId: string,
  ): Promise<{ preferences: NotificationPreferences }> {
    const config = await getBasicAuthConfig(this.auth0Client);
    const params = new URLSearchParams();

    params.append("userId", userId);

    return axios
      .get<{
        preferences: NotificationPreferences;
      }>(
        `${apiConfig.notificationServiceBase}/notifications/preferences?${params.toString()}`,
        config,
      )
      .then((res) => res.data);
  }

  async updateNotificationPreferences(
    user: { identifier: string; display?: string },
    organization: { identifier: string; display?: string },
    preferences: NotificationPreferences,
  ): Promise<{ success: boolean; preferences: NotificationPreferences }> {
    const config = await getBasicAuthConfig(this.auth0Client);

    const userReference = createReference(
      "Practitioner",
      user.identifier,
      user.display,
    );

    const organizationReference = createReference(
      "Organization",
      organization.identifier,
      organization.display,
    );

    return axios
      .put<{ success: boolean; preferences: NotificationPreferences }>(
        `${apiConfig.notificationServiceBase}/notifications/preferences`,
        {
          user: userReference,
          organization: organizationReference,
          preferences,
        },
        config,
      )
      .then((res) => res.data);
  }

  async healthCheck() {
    try {
      const response = await axios.get(
        `${apiConfig.notificationServiceBase}/health`,
      );
      return response.data;
    } catch (e) {
      console.error(
        "Notification Service health check was unsuccessful" +
          JSON.stringify(e),
      );
    }
  }
}

export const notificationApi = new NotificationApi();
